import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import { useLatest } from 'ahooks';
import { useTracker } from '@archipro-website/tracker';
import type { action } from '~/routes/remix-api.home.toggle-home-pin';
import { TOGGLE_HOME_PIN } from '~/routes/remix-api.home.toggle-home-pin';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { useUserLoggedIn } from '~/modules/user';
import { useAppData } from '~/modules/root/hook/use-app-data';

type CallbackFn = (error: { __typename: string; Message: string }) => void;
type SuccessCallbackFn = (pinned: boolean) => void;

const HOME_PIN_TOGGLE_ERROR_TYPENAMES = new Set([
    'UserError',
    'NotAuthorizedError',
    'ExceedLimitError',
]);

/**
 * Hook to generate the function to toggle the home pin status of a directory
 * item. The function will be `undefined` if the current user does not have
 * the required admin permissions (IsStaffAdmin or IsHighAdmin).
 * @param onError Callback function when an error occurs during the API call.
 * @param onSuccess Callback function when the toggle succeeds.
 */
export function useToggleHomePin(
    onError?: CallbackFn,
    onSuccess?: SuccessCallbackFn,
) {
    const fetcher = useFetcher<typeof action>();
    const onErrorRef = useLatest<CallbackFn | undefined>(onError);
    const onSuccessRef = useLatest<SuccessCallbackFn | undefined>(onSuccess);
    const tracker = useTracker();
    const { user, isLoggedIn } = useUserLoggedIn();
    const { user: appUser } = useAppData();

    const isStaffAdmin =
        appUser?.__typename === 'Me' && appUser.IsStaffAdmin === true;
    const isHighAdmin =
        appUser?.__typename === 'Me' &&
        'IsHighAdmin' in appUser &&
        appUser.IsHighAdmin === true;
    const canPinToHomepage = isStaffAdmin || isHighAdmin;

    const eventData = useRef({
        ItemID: 0,
        ExtraData: {
            pinned: false,
            userId: 0,
            userName: '',
        },
    });
    const lastHandledDataRef = useRef<unknown>(null);
    const lastSubmittedPinRef = useRef<boolean>(false);

    const toggleHomePin = useCallback(
        (itemID: number, pin: boolean) => {
            if (itemID <= 0) {
                return;
            }
            lastSubmittedPinRef.current = pin;
            eventData.current = {
                ItemID: itemID,
                ExtraData: {
                    pinned: pin,
                    userId: user.ID,
                    userName: isLoggedIn
                        ? `${user.FirstName} ${user.LastName}`
                        : '',
                },
            };
            fetcher.submit(
                {
                    itemID: itemID,
                    pin: pin,
                },
                {
                    method: 'post',
                    action: TOGGLE_HOME_PIN,
                },
            );
        },
        [fetcher, user, isLoggedIn],
    );

    useEffect(() => {
        if (!fetcherIsDone(fetcher) || !fetcher.data) {
            return;
        }
        if (lastHandledDataRef.current === fetcher.data) {
            return;
        }
        lastHandledDataRef.current = fetcher.data;

        const result = fetcher.data.HomePinToggle;
        if (
            result?.__typename &&
            HOME_PIN_TOGGLE_ERROR_TYPENAMES.has(result.__typename)
        ) {
            onErrorRef.current?.(result);
            return;
        }

        if (result?.__typename) {
            tracker.log('HomePinToggle', {
                url: new URL(window.location.href),
                data: eventData.current,
            });
            onSuccessRef.current?.(lastSubmittedPinRef.current);
        }
    }, [fetcher, onErrorRef, onSuccessRef, tracker]);

    return {
        toggleHomePin: canPinToHomepage ? toggleHomePin : undefined,
    };
}
